import PlayheadTrack from "@app/store/mapLayers/userLocations/playback/types/PlayheadTrack";

export interface PlayheadFrame {
  // Updated cursor (index of the latest point at/behind virtualTimeMs); feed back next frame.
  cursor: number;
  // Start of the full-opacity head segment (the last visited point) — null when parked/idle so the
  // head segment is hidden.
  headStart: google.maps.LatLngLiteral | null;
  // Marker position this frame — null when the rep has no point yet at virtualTimeMs.
  position: google.maps.LatLngLiteral | null;
}

const toLatLng = (coordinates: [number, number]): google.maps.LatLngLiteral => ({
  lat: coordinates[1],
  lng: coordinates[0],
});

const lerp = (from: number, to: number, fraction: number): number => from + (to - from) * fraction;

// Index of the latest point with timeMs <= virtualTimeMs, using `cursor` as a hint. Walks forward
// during play (O(1) amortized) and walks back on a backward scrub. Linear from the hint; bounded by
// track length, which is small per rep.
const locateIndex = (
  points: PlayheadTrack["points"],
  virtualTimeMs: number,
  cursor: number
): number => {
  let index = Math.min(Math.max(cursor, 0), points.length - 1);
  while (index > 0 && points[index].timeMs > virtualTimeMs) {
    index -= 1;
  }
  while (index < points.length - 1 && points[index + 1].timeMs <= virtualTimeMs) {
    index += 1;
  }
  return index;
};

// Pure per-frame placement for one rep at a given playhead time. Returns only plain literals (no
// google.maps runtime objects) so it is safe to call every frame and trivial to unit test. A day
// change between bracketing points is an undrawn gap, so the marker parks at the day's last point.
const interpolateTrackPosition = (
  track: PlayheadTrack,
  virtualTimeMs: number,
  cursor: number
): PlayheadFrame => {
  const { points } = track;
  if (points.length === 0 || virtualTimeMs < points[0].timeMs) {
    return { cursor: 0, headStart: null, position: null };
  }

  const index = locateIndex(points, virtualTimeMs, cursor);
  const previous = points[index];

  // Parked at the last point (also covers a single-point track).
  if (index >= points.length - 1) {
    return {
      cursor: index,
      headStart: null,
      position: toLatLng(previous.coordinates),
    };
  }

  const next = points[index + 1];

  // Cross-day gap: no polyline connects the two points, so park at the day's last point.
  if (previous.day !== next.day) {
    return {
      cursor: index,
      headStart: null,
      position: toLatLng(previous.coordinates),
    };
  }

  const span = next.timeMs - previous.timeMs;
  const fraction = span > 0 ? (virtualTimeMs - previous.timeMs) / span : 0;
  return {
    cursor: index,
    headStart: toLatLng(previous.coordinates),
    position: {
      lat: lerp(previous.coordinates[1], next.coordinates[1], fraction),
      lng: lerp(previous.coordinates[0], next.coordinates[0], fraction),
    },
  };
};

export default interpolateTrackPosition;
